import { useState, useEffect, useCallback } from "react";
import { generateFakePirSeries } from "../fakeData";
import { apiFetch } from "../apiFetch";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

function formatTimestamp(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function formatDuration(seconds) {
  const s = parseFloat(seconds);
  if (!Number.isFinite(s)) return null;
  if (s < 60) return `${s.toFixed(0)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

// The /api/sensors/:deviceId endpoint returns an array of historical records
// when a range is asked for (#200) and a single latest record when one is not.
// Both are normalised to an array, so this keeps rendering against a server that
// predates the range support rather than throwing.
function normalize(json) {
  if (Array.isArray(json)) return json;
  if (json && typeof json === "object" && !json.error) return [json];
  return [];
}

// `simulated` renders from generated data and makes no network request — see
// the note on TowerDriftChart and fakeData.js.
export default function PIREventLog({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [range, setRange] = useState("7d");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // The API caps how far back one request may page. When it stops short it says
  // so, and an event count drawn from a partial window has to say so too — a
  // badge reading "19 events" for a window the server never finished reading is
  // the same class of wrong number this panel had before #200.
  const [truncated, setTruncated] = useState(false);

  const RANGE_OPTIONS = [
    { label: t("range24h"), value: "24h" },
    { label: t("range7d"), value: "7d" },
    { label: t("range30d"), value: "30d" },
  ];

  const fetchData = useCallback(async () => {
    try {
      let json;
      let cutShort = false;
      if (simulated) {
        json = generateFakePirSeries(deviceId, range);
      } else {
        const url = `${apiBaseUrl}/api/sensors/${encodeURIComponent(deviceId)}?range=${range}`;
        const res = await apiFetch(url);
        if (!res.ok) throw new Error(`API error ${res.status}`);
        // Optional-chained: a server from before #200 sends no such header, and
        // "absent" means the same thing here as "false".
        cutShort = res.headers?.get("X-Readings-Truncated") === "true";
        json = await res.json();
      }
      setTruncated(cutShort);
      const triggers = normalize(json)
        .filter((d) => d?.payload?.pir === "trigger")
        .map((d) => ({
          ts: d.SK ?? d.timestamp,
          duration: d.payload?.duration ?? d.payload?.motion_duration ?? null,
        }))
        .filter((e) => e.ts)
        .sort((a, b) => new Date(b.ts) - new Date(a.ts)); // newest first
      setEvents(triggers);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, range, simulated]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div style={{ ...styles.card, maxWidth: 1600 }}>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{label ?? deviceId}</h2>
          <p style={styles.subtitle}>{deviceId}</p>
        </div>
        {!loading && !error && (
          <span style={styles.countBadge}>
            {events.length} {events.length === 1 ? "event" : "events"}
          </span>
        )}
      </div>

      {/* Range selector */}
      <div style={styles.rangeRow}>
        {RANGE_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setRange(opt.value)}
            style={range === opt.value ? styles.rangeActive : styles.rangeBtn}>
            {opt.label}
          </button>
        ))}
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {/* Partial window */}
      {!loading && !error && truncated && (
        <div style={styles.truncatedNote}>
          This window was too large to read in full, so the count covers the most
          recent events only. Older events in the range are not included.
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : events.length === 0 ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : (
        <ul style={styles.timeline}>
          {events.map((e, idx) => {
            const dur = formatDuration(e.duration);
            return (
              <li key={`${e.ts}-${idx}`} style={styles.event}>
                <span style={styles.dot} />
                <div style={styles.eventBody}>
                  <span style={styles.eventTitle}>Motion detected</span>
                  <span style={styles.eventTime}>{formatTimestamp(e.ts)}</span>
                </div>
                {dur && <span style={styles.durationBadge}>{dur}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  countBadge: { background: "#eff6ff", color: "#1d4ed8", padding: "3px 10px", borderRadius: 12, fontSize: 13, fontWeight: 500 },
  rangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 16 },
  rangeBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13 },
  rangeActive: { padding: "4px 12px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  truncatedNote: { marginBottom: 12, padding: "8px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, color: "#92400e", fontSize: 12, lineHeight: 1.5 },
  placeholder: { height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  timeline: { listStyle: "none", margin: 0, padding: 0, maxHeight: 360, overflowY: "auto", borderLeft: "2px solid #e5e7eb", marginLeft: 6 },
  event: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", position: "relative" },
  dot: { width: 10, height: 10, borderRadius: "50%", background: "#3b82f6", flexShrink: 0, marginLeft: -7, border: "2px solid #ffffff", boxShadow: "0 0 0 1px #e5e7eb" },
  eventBody: { display: "flex", flexDirection: "column", gap: 2 },
  eventTitle: { fontSize: 14, fontWeight: 600, color: "#111827" },
  eventTime: { fontSize: 12, color: "#6b7280" },
  durationBadge: { marginLeft: "auto", background: "#f3f4f6", color: "#374151", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 500 },
};
